/**
 * SEO Analyzer Utility
 * Analyzes content for SEO best practices and returns a score
 */

// Calculate reading time based on word count
export const calculateReadingTime = (text) => {
  const wordsPerMinute = 200;
  const words = text.trim().split(/\s+/).length;
  return Math.ceil(words / wordsPerMinute);
};

// Count words in text
export const countWords = (text) => {
  return text.trim().split(/\s+/).filter(word => word.length > 0).length;
};

// Calculate Flesch Reading Ease score
export const calculateReadability = (text) => {
  const sentences = text.split(/[.!?]+/).filter(s => s.trim().length > 0).length;
  const words = countWords(text);
  const syllables = countSyllables(text);
  
  if (sentences === 0 || words === 0) return 0;
  
  const score = 206.835 - 1.015 * (words / sentences) - 84.6 * (syllables / words);
  return Math.max(0, Math.min(100, score));
};

// Count syllables (simplified)
const countSyllables = (text) => {
  const words = text.toLowerCase().match(/\b[a-z]+\b/g) || [];
  let syllableCount = 0;
  
  words.forEach(word => {
    syllableCount += word.replace(/(?:[^laeiouy]es|ed|[^laeiouy]e)$/, '')
      .replace(/^y/, '')
      .match(/[aeiouy]{1,2}/g)?.length || 1;
  });
  
  return syllableCount;
};

// Get readability level
export const getReadabilityLevel = (score) => {
  if (score >= 90) return { level: 'very easy', color: 'green' };
  if (score >= 80) return { level: 'easy', color: 'green' };
  if (score >= 70) return { level: 'fairly easy', color: 'yellow' };
  if (score >= 60) return { level: 'standard', color: 'yellow' };
  if (score >= 50) return { level: 'fairly difficult', color: 'orange' };
  if (score >= 30) return { level: 'difficult', color: 'red' };
  return { level: 'very difficult', color: 'red' };
};

// Analyze SEO score
export const analyzeSEO = (data) => {
  const scores = {
    title: 0,
    description: 0,
    content: 0,
    keywords: 0,
    readability: 0,
    structure: 0
  };
  
  const feedback = [];
  
  // Title analysis (25 points)
  if (data.title) {
    const titleLength = data.title.length;
    if (titleLength >= 30 && titleLength <= 60) {
      scores.title = 25;
      feedback.push({ type: 'success', message: 'Title length is optimal (30-60 characters)' });
    } else if (titleLength > 0) {
      scores.title = 15;
      if (titleLength < 30) {
        feedback.push({ type: 'warning', message: 'Title is too short. Aim for 30-60 characters.' });
      } else {
        feedback.push({ type: 'warning', message: 'Title is too long. Aim for 30-60 characters.' });
      }
    } else {
      feedback.push({ type: 'error', message: 'Title is missing.' });
    }
  }
  
  // SEO Title analysis
  if (data.seoTitle) {
    const seoTitleLength = data.seoTitle.length;
    if (seoTitleLength >= 50 && seoTitleLength <= 60) {
      scores.title += 5;
    }
  }
  
  // Meta Description analysis (20 points)
  if (data.seoDescription || data.excerpt) {
    const description = data.seoDescription || data.excerpt;
    const descLength = description.length;
    if (descLength >= 120 && descLength <= 160) {
      scores.description = 20;
      feedback.push({ type: 'success', message: 'Meta description length is optimal (120-160 characters)' });
    } else if (descLength > 0) {
      scores.description = 10;
      if (descLength < 120) {
        feedback.push({ type: 'warning', message: 'Meta description is too short. Aim for 120-160 characters.' });
      } else {
        feedback.push({ type: 'warning', message: 'Meta description is too long. Aim for 120-160 characters.' });
      }
    } else {
      feedback.push({ type: 'error', message: 'Meta description is missing.' });
    }
  }
  
  // Content analysis (25 points)
  if (data.content) {
    const wordCount = countWords(data.content);
    const hasImages = (data.content.match(/<img/g) || []).length > 0;
    const hasHeadings = (data.content.match(/<h[1-6]/g) || []).length > 0;
    const hasLinks = (data.content.match(/<a /g) || []).length > 0;
    
    if (wordCount >= 300) {
      scores.content = 15;
      feedback.push({ type: 'success', message: `Content has ${wordCount} words (minimum 300 recommended)` });
    } else if (wordCount > 0) {
      scores.content = 8;
      feedback.push({ type: 'warning', message: `Content is too short (${wordCount} words). Aim for at least 300 words.` });
    }
    
    if (hasImages) {
      scores.content += 3;
      feedback.push({ type: 'success', message: 'Content includes images' });
    } else {
      feedback.push({ type: 'info', message: 'Consider adding images to improve engagement' });
    }
    
    if (hasHeadings) {
      scores.content += 4;
      feedback.push({ type: 'success', message: 'Content has proper heading structure' });
    } else {
      feedback.push({ type: 'warning', message: 'Add headings (H2, H3) to structure your content' });
    }
    
    if (hasLinks) {
      scores.content += 3;
    }
  } else {
    feedback.push({ type: 'error', message: 'Content is missing.' });
  }
  
  // Keywords analysis (10 points)
  if (data.tags && data.tags.length > 0) {
    scores.keywords = 10;
    feedback.push({ type: 'success', message: `${data.tags.length} keywords/tags added` });
  } else {
    feedback.push({ type: 'warning', message: 'Add keywords/tags to improve SEO' });
  }
  
  // Readability analysis (15 points)
  if (data.content) {
    const readabilityScore = calculateReadability(data.content);
    const readability = getReadabilityLevel(readabilityScore);
    
    if (readabilityScore >= 60) {
      scores.readability = 15;
      feedback.push({ type: 'success', message: `Content readability is ${readability.level} (score: ${Math.round(readabilityScore)})` });
    } else if (readabilityScore >= 40) {
      scores.readability = 10;
      feedback.push({ type: 'warning', message: `Content readability is ${readability.level}. Consider simplifying sentences.` });
    } else {
      scores.readability = 5;
      feedback.push({ type: 'warning', message: 'Content is difficult to read. Use shorter sentences and simpler words.' });
    }
  }
  
  // Structure analysis (5 points)
  if (data.category) {
    scores.structure = 3;
  }
  if (data.featuredImage) {
    scores.structure += 2;
    feedback.push({ type: 'success', message: 'Featured image added' });
  } else {
    feedback.push({ type: 'warning', message: 'Add a featured image' });
  }
  
  // Calculate total score
  const totalScore = Object.values(scores).reduce((sum, score) => sum + score, 0);
  
  return {
    totalScore: Math.round(totalScore),
    breakdown: scores,
    feedback,
    wordCount: data.content ? countWords(data.content) : 0,
    readingTime: data.content ? calculateReadingTime(data.content) : 0,
    readability: data.content ? getReadabilityLevel(calculateReadability(data.content)) : null
  };
};
